import type { JSX } from 'solid-js';
import { Show } from 'solid-js';
import { t } from '../i18n';
import { escalationTiers, policySections } from '../lib/policy-view';
import { useRepoPolicy } from '../lib/repo-detail';
import { EmptyState } from './EmptyState';
import { EscalationRow } from './EscalationRow';
import { Panel } from './Panel';
import { PolicyFallbackAlert } from './PolicyFallbackAlert';
import { PolicyLintPanel } from './PolicyLintPanel';
import { PolicySections } from './PolicySections';
import { QueryBoundary } from './QueryBoundary';
import type { RepoTabProps } from './RepoOverviewTab';
import { Skeleton } from './Skeleton';

// Repo detail → Policy (#195, arc 101 slice 06): the active resolved snapshot,
// read-only. Editing stays in `.maintainer.yml` in the repo, per principles
// ("policy = prompt + tools").
//
// Three parts, in order of what a maintainer came for:
//   • the lint control — "does my policy still pass today's rules?" — OR, when the
//     snapshot is broken (#3911), the alert naming why and saying the sections
//     below are the platform defaults. The lint is dropped there: it re-checks
//     what the tab shows, which would be the defaults, and report "still passes";
//   • EVERY section of the snapshot, rendered generically from the snapshot's own
//     keys (`lib/policy-view.ts`), so no field is invisible and none is renamed;
//   • the escalation matrix, the one curated view (tone-coded tiers) — `escalate`
//     is therefore excluded from the generic grid rather than printed twice.

export type RepoPolicyTabProps = RepoTabProps & {
  /**
   * Is the stored snapshot still valid against the repo's current state? Carried
   * in by `<RepoDetail/>` from the `useRepo` row — the same row the readiness +
   * secret gates already wait on, so no new query is needed. Three states:
   * `true` = snapshot valid, `false` = snapshot stored but drifted, `null` = no
   * snapshot. Tabs that render ONLY the resolved snapshot (this one) can ignore
   * it; tabs that branch on the stored-vs-missing distinction read it from
   * here. Optional so existing call sites (the few tests that render this tab
   * without a backing `useRepo` row) keep compiling; `| undefined` is explicit
   * so the JSX site (`repo.data?.policy_valid`) type-checks under
   * `exactOptionalPropertyTypes` — the value is `boolean | null | undefined`
   * before the route filters it.
   */
  readonly policy_valid?: boolean | null | undefined;
};

export function RepoPolicyTab(props: RepoPolicyTabProps): JSX.Element {
  const policy = useRepoPolicy(() => props.encoded, props.token);

  return (
    <QueryBoundary query={policy} loading={<Skeleton height="20rem" />}>
      {(snapshot) => (
        <Show
          when={snapshot}
          fallback={
            <EmptyState
              message={t('repo_detail.policy_missing')}
              ctaLabel={t('repo_detail.policy_missing_cta')}
              // A missing .maintainer.yml is fixed in the repo, not by
              // re-running app onboarding (#377) — send to the reference doc.
              ctaHref="https://www.developerz.ai/docs/maintainer-yml"
            />
          }
        >
          {(p) => (
            <div class="repo-detail__stack">
              <Show
                when={p().fallback}
                fallback={
                  <PolicyLintPanel
                    encoded={props.encoded}
                    token={props.token}
                    snapshot={p().policy}
                  />
                }
              >
                {(f) => <PolicyFallbackAlert fallback={f()} />}
              </Show>

              <PolicySections sections={policySections(p().policy)} />

              <Panel
                title={t('repo_detail.policy_escalation')}
                footer={t('repo_detail.policy_source')}
                class="card--footered"
              >
                <ul class="repo-policy__matrix">
                  <EscalationRow
                    tone="danger"
                    label={t('repo_detail.policy_escalate_always')}
                    cats={escalationTiers(p().policy).always}
                  />
                  <EscalationRow
                    tone="neutral"
                    label={t('repo_detail.policy_escalate_ask')}
                    cats={escalationTiers(p().policy).ask}
                  />
                </ul>
              </Panel>
            </div>
          )}
        </Show>
      )}
    </QueryBoundary>
  );
}
